import { useMutation, useQuery, useReactiveVar } from '@apollo/client'
import React, { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { jwtDecode } from 'jwt-decode'

import { useMarketplace } from '@src/hooks/useMarketplace'
import { MainRouteName } from '@src/hooks/useAccountRouter'
import { Footer } from '@src/components/Footer/Footer'
import { LoadingSpinner } from '@src/components/Loaders/LoadingSpinner'
import { MerchantType } from '@src/graphql-types'
import { jwtVar } from '@src/models/customer/jwt'

import { ArrowRightSVG } from '@src/components/SVGS/ArrowRightSVG'
import { Button, ButtonType } from '@src/components/Button/Button'
import { imageJitURL } from '@src/utils/imageJitURL'
import { CustomerMarketingStatusDocument } from './graphql/__generated__/customerMarketingStatus.query.graphql-interface'
import { UpdateMarketingOptOutDocument } from './graphql/__generated__/updateMarketingOptOut.mutation.graphql-interface'
import { UndoMarketingOptOutDocument } from './graphql/__generated__/undoMarketingOptOut.mutation.graphql-interface'

import {
  PageContainer,
  SplitLayout,
  Aside,
  Logo,
  AsideBody,
  Kicker,
  AsideTitle,
  AsideText,
  AppBadges,
  Main,
  PageTitle,
  Lede,
  StepIndicator,
  Tail,
  LeadButton,
  TextButton,
  ButtonRow,
  Divider,
  OptOutCopy,
  EffectsList,
  Effect,
  EffectMark,
  EffectContent,
  EffectTitle,
  EffectNote,
  LoadingContainer,
  ErrorContainer,
} from './OptOut.styles'

import { AppStoreSVG } from '../../components/SVGS/AppStoreSVG'
import { PlayStoreSVG } from '../../components/SVGS/PlayStoreSVG'

type Channel = 'sms' | 'email'
type MutationChannel = 'SMS' | 'EMAIL'
type Step = 'landing' | 'confirm' | 'done'

type OptOutPageProps = {
  channel: Channel
}

export const OptOutPage: React.FC<OptOutPageProps> = ({ channel }) => {
  const { customerId } = useParams<{ customerId: string }>()
  const navigate = useNavigate()
  const { t } = useTranslation('optOut')

  const {
    logoImage,
    name,
    storeURLApple,
    storeURLGooglePlay,
    merchantType,
    urlPath,
  } = useMarketplace()

  const [step, setStep] = useState<Step>('landing')
  const [hasAutoOptedOut, setHasAutoOptedOut] = useState(false)
  // Undo is only offered for the opt-out that happened in this session.
  const [undoToken, setUndoToken] = useState<string | null>(null)

  const businessTypeLabel =
    merchantType === MerchantType.RETAIL
      ? t('businessType.shops')
      : t('businessType.restaurants')

  const { data, loading, error, refetch } = useQuery(
    CustomerMarketingStatusDocument,
    {
      variables: {
        customerId: customerId ?? '',
      },
      skip: !customerId,
    }
  )

  const mutationChannel: MutationChannel = channel === 'email' ? 'EMAIL' : 'SMS'

  const [optOut, { loading: optingOut }] = useMutation(
    UpdateMarketingOptOutDocument,
    {
      variables: {
        customerId: customerId ?? '',
        channel: mutationChannel,
      },
      onCompleted: data => {
        setUndoToken(data.updateMarketingOptOut.undoToken)

        if (channel === 'email') {
          void refetch()
          return
        }

        setStep('done')
      },
    }
  )

  const [optIn, { loading: optingIn }] = useMutation(
    UndoMarketingOptOutDocument,
    {
      onCompleted: () => {
        setUndoToken(null)

        if (channel === 'email') {
          void refetch()
          return
        }

        setStep('landing')
      },
    }
  )

  const handleUndo = () => {
    if (!undoToken) {
      return
    }

    void optIn({ variables: { undoToken } })
  }

  const handleBrowse = () => {
    navigate(`/${urlPath}`)
  }

  const handleManagePreferences = () => {
    navigate(`/${urlPath}?account=${MainRouteName.MARKETING}`)
  }

  const logoUrl = imageJitURL(logoImage, {
    resize: {
      width: 200,
      height: 100,
      fit: 'outside',
    },
  })

  const maskedPhone = data?.customerMarketingStatus?.maskedPhoneNumber || '••••'
  const maskedEmail = data?.customerMarketingStatus?.maskedEmail || '***@***'
  const alreadyOptedOut =
    channel === 'email'
      ? data?.customerMarketingStatus?.optOutMarketingEmail
      : data?.customerMarketingStatus?.optOutMarketingSMS

  // Check if logged-in user is viewing their own opt-out page
  const jwt = useReactiveVar(jwtVar)
  let loggedInCustomerId: string | null = null

  try {
    loggedInCustomerId = jwt
      ? jwtDecode<{ customer_id: string }>(jwt)?.customer_id
      : null
  } catch {
    loggedInCustomerId = null
  }

  const isLoggedInAsSameCustomer = loggedInCustomerId === customerId

  // Email link behavior intentionally opts out immediately after first successful load.
  useEffect(() => {
    if (channel !== 'email') {
      return
    }

    if (hasAutoOptedOut) {
      return
    }

    if (!data) {
      return
    }

    if (alreadyOptedOut) {
      setHasAutoOptedOut(true)
      return
    }

    if (!optingOut) {
      setHasAutoOptedOut(true)
      void optOut()
    }
  }, [channel, data, alreadyOptedOut, hasAutoOptedOut, optingOut, optOut])

  if (error || !customerId) {
    return (
      <PageContainer>
        <Main>
          <ErrorContainer>
            <h3>{t('error.title')}</h3>
            <p>{t('error.invalidLink')}</p>
          </ErrorContainer>
        </Main>
        <Footer />
      </PageContainer>
    )
  }

  if (
    loading ||
    (channel === 'email' && !alreadyOptedOut && !hasAutoOptedOut)
  ) {
    return (
      <PageContainer>
        <LoadingContainer>
          <LoadingSpinner />
        </LoadingContainer>
      </PageContainer>
    )
  }

  return (
    <PageContainer>
      <SplitLayout>
        <Aside>
          {logoUrl && <Logo src={logoUrl} alt={name} />}
          <AsideBody>
            <Kicker>{t('sidebar.kicker')}</Kicker>
            <AsideTitle>{t('sidebar.title')}</AsideTitle>
            <AsideText>{t('sidebar.description')}</AsideText>
            {(storeURLApple || storeURLGooglePlay) && (
              <AppBadges>
                {storeURLApple && (
                  <a
                    href={storeURLApple}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <AppStoreSVG id={t('sidebar.appStore')} />
                  </a>
                )}
                {storeURLGooglePlay && (
                  <a
                    href={storeURLGooglePlay}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <PlayStoreSVG id={t('sidebar.googlePlay')} />
                  </a>
                )}
              </AppBadges>
            )}
          </AsideBody>
        </Aside>

        <Main>
          {channel === 'sms' && step === 'landing' && !alreadyOptedOut && (
            <div>
              <PageTitle>{t('sms.landing.title')}</PageTitle>
              <Lede>
                {t('sms.landing.summary', {
                  businessType: businessTypeLabel.toLowerCase(),
                })}
              </Lede>

              <LeadButton $fullWidth onClick={handleBrowse}>
                <span>
                  {t('sms.landing.browseButton', {
                    businessType: businessTypeLabel,
                  })}
                </span>

                <ArrowRightSVG />
              </LeadButton>
              <Tail>
                <Divider>
                  <span>{t('sms.landing.or')}</span>
                </Divider>
                <OptOutCopy>{t('sms.landing.optOutCopy')}</OptOutCopy>
                <Button
                  buttonType={ButtonType.SECONDARY}
                  content={t('sms.landing.optOutButton', {
                    phone: maskedPhone,
                  })}
                  onClick={() => setStep('confirm')}
                />
              </Tail>
            </div>
          )}

          {channel === 'sms' && step === 'confirm' && !alreadyOptedOut && (
            <div>
              <StepIndicator>{t('sms.confirm.step')}</StepIndicator>
              <PageTitle style={{ marginTop: 12 }}>
                {t('sms.confirm.title')}
              </PageTitle>
              <Lede>{t('sms.confirm.summary', { phone: maskedPhone })}</Lede>

              <EffectsList>
                <Effect>
                  <EffectMark>✕</EffectMark>
                  <EffectContent>
                    <EffectTitle>
                      {t('sms.confirm.effects.noOffers.title')}
                    </EffectTitle>
                    <EffectNote>
                      {t('sms.confirm.effects.noOffers.note')}
                    </EffectNote>
                  </EffectContent>
                </Effect>
                <Effect>
                  <EffectMark $positive>✓</EffectMark>
                  <EffectContent>
                    <EffectTitle>
                      {t('sms.confirm.effects.orderUpdates.title')}
                    </EffectTitle>
                    <EffectNote>
                      {t('sms.confirm.effects.orderUpdates.note')}
                    </EffectNote>
                  </EffectContent>
                </Effect>
                <Effect>
                  <EffectMark $positive>✓</EffectMark>
                  <EffectContent>
                    <EffectTitle>
                      {t('sms.confirm.effects.deliveryUpdates.title')}
                    </EffectTitle>
                    <EffectNote>
                      {t('sms.confirm.effects.deliveryUpdates.note')}
                    </EffectNote>
                  </EffectContent>
                </Effect>
              </EffectsList>

              <ButtonRow>
                <Button
                  buttonType={ButtonType.DANGER}
                  content={
                    optingOut
                      ? t('common.loading')
                      : t('sms.confirm.confirmButton')
                  }
                  onClick={() => optOut()}
                  loading={optingOut}
                />
                <Button
                  buttonType={ButtonType.SECONDARY}
                  content={t('sms.confirm.cancelButton')}
                  onClick={() => setStep('landing')}
                />
              </ButtonRow>
            </div>
          )}

          {channel === 'sms' && (step === 'done' || alreadyOptedOut) && (
            <div role="status">
              <StepIndicator $done>{t('sms.done.step')}</StepIndicator>
              <PageTitle style={{ marginTop: 12 }}>
                {t('sms.done.title')}
              </PageTitle>
              <Lede>{t('sms.done.summary', { phone: maskedPhone })}</Lede>

              {undoToken && (
                <TextButton
                  onClick={handleUndo}
                  disabled={optingIn}
                  style={{ marginTop: 18 }}
                >
                  {optingIn ? t('common.loading') : t('sms.done.undoButton')}
                </TextButton>
              )}

              <LeadButton
                $fullWidth
                onClick={handleBrowse}
                style={{ marginTop: 28 }}
              >
                <span>
                  {t('sms.done.browseButton', {
                    businessType: businessTypeLabel,
                  })}
                </span>

                <ArrowRightSVG />
              </LeadButton>
            </div>
          )}

          {channel === 'email' && (
            <div role="status">
              <StepIndicator $done>{t('email.done.step')}</StepIndicator>
              <PageTitle style={{ marginTop: 12 }}>
                {t('email.done.title')}
              </PageTitle>
              <Lede>{t('email.done.summary', { email: maskedEmail })}</Lede>

              {undoToken && (
                <TextButton
                  onClick={handleUndo}
                  disabled={optingIn}
                  style={{ marginTop: 18 }}
                >
                  {optingIn ? t('common.loading') : t('email.done.undoButton')}
                </TextButton>
              )}

              <LeadButton
                $fullWidth
                onClick={handleBrowse}
                style={{ marginTop: 28 }}
              >
                <span>
                  {t('email.done.browseButton', {
                    businessType: businessTypeLabel,
                  })}
                </span>

                <ArrowRightSVG />
              </LeadButton>
              {isLoggedInAsSameCustomer && (
                <TextButton
                  onClick={handleManagePreferences}
                  style={{ marginTop: 24 }}
                  data-test-id="optout-email-button-managepreferences"
                >
                  {t('common.manageAllPreferences')}
                </TextButton>
              )}
            </div>
          )}

          {channel === 'sms' && isLoggedInAsSameCustomer && (
            <TextButton
              onClick={handleManagePreferences}
              style={{ marginTop: 24 }}
              data-test-id="optout-sms-button-managepreferences"
            >
              {t('common.manageAllPreferences')}
            </TextButton>
          )}
        </Main>
      </SplitLayout>
    </PageContainer>
  )
}
